import { parseChatMessage, parseToPlainText } from '@missionlabs/smartagent-chat-components-lib'
import { getChatAttachmentUrl } from 'services/api'
import { MiddlewareStore } from 'store/createStore'
import { capitaliseString, downloadFile, getFilename, isFileReadyToDownload } from 'utils'
import removeMd from 'remove-markdown'
import * as Logger from '../../../services/logger'
import { DEFAULT_LANGUAGE } from 'translations'
import { formatDateTime } from '@missionlabs/smartagent-app-components'
import { SAMessageType } from "@missionlabs/smartagent-chat-components-lib/dist/ChatMessage/chat-message.types"

const exportTranscript = async (store: MiddlewareStore) => {
    const messages = store.getState().messages
    const {
        facia,
        company,
        systemDisplayName,
        language,
        contactId,
        connectionDetails,
        appTextTranslations,
        previousContactID,
    } = store.getState().app
    let attachmentCounter = 0
    const attachmentsMap = {} as Record<string, string>

    messages.forEach(({ type, content }, index) => {
        if (type === 'ATTACHMENT') {
            if (!isFileReadyToDownload(content as SAMessageType)) {
                return
            }

            const localAttachmentsIds = localStorage.getItem(
                `smartagent_signed_attachment_ids/${connectionDetails.contactFlowId}`,
            )
            if (localAttachmentsIds) {
                const attachmentConfig = JSON.parse(localAttachmentsIds)

                if (contactId && attachmentConfig[contactId]?.length) {
                    const attachmentId = attachmentConfig[contactId][attachmentCounter]
                    attachmentCounter++
                    attachmentsMap[index] = attachmentId
                } else if (previousContactID && attachmentConfig[previousContactID]?.length) {
                    const attachmentId = attachmentConfig[previousContactID][attachmentCounter]
                    attachmentCounter++
                    attachmentsMap[index] = attachmentId
                }
            }
        }
    })

    try {
        const parsedMessages = await Promise.all(
            messages.map(async (message, index) => {
                const { displayName, content, sentAt, role, type } = message

                if (
                    !content ||
                    !displayName ||
                    JSON.stringify(content)?.includes('SILENT_STATE_UPDATE') ||
                    JSON.stringify(content)?.includes('INVISIBLE_MESSAGE')
                )
                    return null

                let participant = 'unknown'
                switch (role) {
                    case 'CUSTOMER':
                        participant =
                            appTextTranslations!.metaMessages![language ?? DEFAULT_LANGUAGE].you
                        break
                    case 'SYSTEM':
                        participant = systemDisplayName ?? capitaliseString(facia ?? company)
                        break
                    case 'AGENT':
                        participant = 'Agent'
                        break
                    default:
                        throw new Error('unrecognised participant role ' + message.role)
                }

                let parsed = content
                if (typeof content === 'object') {
                    const contactLanguage = language ?? DEFAULT_LANGUAGE
                    parsed = parseChatMessage(
                        JSON.stringify({ [contactLanguage]: content }),
                        parseToPlainText({
                            translations: {
                                detailsText:
                                    appTextTranslations?.detailsText?.[language] ??
                                    appTextTranslations?.detailsText?.[DEFAULT_LANGUAGE]!,
                                optionsText:
                                    appTextTranslations?.optionsText?.[language] ??
                                    appTextTranslations?.optionsText?.[DEFAULT_LANGUAGE]!,
                                buttonsText:
                                    appTextTranslations?.buttonsText?.[language] ??
                                    appTextTranslations?.buttonsText?.[DEFAULT_LANGUAGE]!,
                            },
                        }),
                    )
                }
                const removedQuotes = JSON.stringify(parsed).replace(/"/gm, '')
                const removedNewLines = removedQuotes.replace(/\\n/gm, ' ')
                const removedMarkdown = removeMd(removedNewLines)

                let result = removedMarkdown

                if (type === 'ATTACHMENT') {
                    if (!isFileReadyToDownload(content as SAMessageType)) {
                        return null
                    }

                    // The chat participant signed url only lasts a few minutes but we can pipe the key to our lambda
                    const senderType = role === 'CUSTOMER' ? 'customer' : 'agent'
                    const response = await getChatAttachmentUrl(
                        company,
                        facia,
                        encodeURI(attachmentsMap[index]),
                        senderType,
                    )

                    if (response?.data?.objectUrl)
                        result = `${result} <a href='${response?.data?.objectUrl}'>Download file<a/>`
                }
                const date = new Date(sentAt)

                return `<div style="margin-bottom:30px; margin-left:15px"><div><b>${participant}</b> ${formatDateTime(
                    date,
                    { time: true, date: false },
                )}</div><hr /> ${result?.trim()}</div>`
            }),
        )

        const filteredMessages = parsedMessages.filter((value) => value !== null).join('\n')

        const chatTranscript =
            appTextTranslations!.chatTranscript?.[language ?? DEFAULT_LANGUAGE] ??
            appTextTranslations!.chatTranscript?.[DEFAULT_LANGUAGE]

        const header = `<div style="background: #000;color: #fff;padding: 0px 25px 0px 15px;justify-content: space-between;display: flex;align-items: center;margin-bottom: 20px;"><h1>${chatTranscript}</h1><svg onclick="window.print()" xmlns="http://www.w3.org/2000/svg" height="48" width="48" style="fill: #fff;float: right;cursor: pointer;"><path d="M32.9 15.6V9H15.1v6.6h-3V6h23.8v9.6ZM7 18.6h34-28.9Zm29.95 4.75q.6 0 1.05-.45.45-.45.45-1.05 0-.6-.45-1.05-.45-.45-1.05-.45-.6 0-1.05.45-.45.45-.45 1.05 0 .6.45 1.05.45.45 1.05.45ZM32.9 39v-9.6H15.1V39Zm3 3H12.1v-8.8H4V20.9q0-2.25 1.525-3.775T9.3 15.6h29.4q2.25 0 3.775 1.525T44 20.9v12.3h-8.1ZM41 30.2v-9.3q0-1-.65-1.65-.65-.65-1.65-.65H9.3q-1 0-1.65.65Q7 19.9 7 20.9v9.3h5.1v-3.8h23.8v3.8Z"></path></svg></div>`

        const chatInitiated = new Date(messages[0].sentAt)

        const chatStartedMessage =
            appTextTranslations!.chatStarted?.[language ?? DEFAULT_LANGUAGE] ??
            appTextTranslations!.chatStarted?.[DEFAULT_LANGUAGE]

        const chatStarted = `<div style=" margin-bottom: 20px; margin-left:15px; font-style: italic;">${chatStartedMessage}: ${formatDateTime(
            chatInitiated,
        )} at ${formatDateTime(chatInitiated, { time: true, date: false })}</div>`

        const documentTitle =
            appTextTranslations!.document?.[language ?? DEFAULT_LANGUAGE] ??
            appTextTranslations!.document?.[DEFAULT_LANGUAGE]

        const document = `<!DOCTYPE html><html lang="en"><head> <meta charset="UTF-8"> <meta http-equiv="X-UA-Compatible" content="IE=edge"> <meta name="viewport" content="width=device-width, initial-scale=1.0"> <title>${documentTitle}</title><link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Rubik:ital,wght@0,400;0,500;1,400;1,500&display=swap" rel="stylesheet"></head><body style="font-family: 'Rubik', sans-serif;margin:0">${header}${chatStarted}${filteredMessages} </body></html>`

        downloadFile(document, getFilename(chatTranscript))
    } catch (error) {
        Logger.error(`Unable to export transcript`, error)
        console.log(error)
    }
}

export default exportTranscript
